import { formatTime, Modal, SubmitButton, Tooltip } from "@xola/ui-kit";
import { produce } from "immer";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { Arrival, Experience, TimeRange, Timeslot } from "types";
import shallow from "zustand/shallow";
import { makeExperiencePurchaseItem } from "../../models/CreatePurchaseState";
import { useCreatePurchaseStore } from "../../stores/createPurchaseStore";
import { ArrivalTimePicker } from "../DatePicker/ArrivalTimePicker";

interface JoinWaitlistAvailableSpotsProps {
    timeslots: Timeslot[] | undefined;
    experience: Experience | undefined;
    timeRange: Partial<TimeRange> & { flexible: boolean };
    quantity: number;
    isLoading: boolean;
    isSuccess: boolean;
    onSubmit: (payload?: JoinWaitlistPayload) => Promise<void>;
    onClose: () => void;
}

export const JoinWaitlistAvailableSpots = ({
    timeslots,
    timeRange,
    experience,
    quantity,
    isLoading,
    isSuccess,
    onSubmit,
    onClose,
}: JoinWaitlistAvailableSpotsProps) => {
    const [selectedTimeslotTime, setSelectedTimeslotTime] = useState<number | null>(null);
    const history = useHistory();
    const [addItem, resetState] = useCreatePurchaseStore(
        (state) => [state.actions.addItem, state.actions.resetState],
        shallow,
    );
    const setSeller = useCreatePurchaseStore((state) => state.actions.setSeller);
    const setPurchase = useCreatePurchaseStore((state) => state.actions.setPurchase);

    const activeItemIndex = useCreatePurchaseStore((state) => state.activeItemIndex); // Is 0 by default
    const purchaseItems = useCreatePurchaseStore((state) => state.purchase.items); // Is empty if outside of cart and unfinished purchase
    const isMatchingExperience =
        purchaseItems.length > 0 && purchaseItems[activeItemIndex]?.product.id === experience?.id; // Is false if outside of cart and unfinished purchase
    const isNewPurchase = !isMatchingExperience;

    const setArrival = (newArrival: Arrival, timeslot?: Timeslot) => {
        setPurchase((draft) => {
            if (draft.items[activeItemIndex]) {
                draft.items[activeItemIndex].arrivalDate = newArrival.date;
                draft.items[activeItemIndex].arrivalTime = newArrival.time ? newArrival.time : undefined;
            }
        });
        if (timeslot) {
            useCreatePurchaseStore.setState(
                produce((draft) => {
                    draft.purchase.items[activeItemIndex].timeslot = timeslot;
                }),
            );
        }
    };

    const tootltipContent = `Join waitlist for ${formatTime(timeRange.startTime)}-${formatTime(timeRange.endTime)}`;

    const handleExperienceSelect = () => {
        if (experience) {
            if (isNewPurchase) {
                resetState();
                setSeller(experience.seller.id);
                addItem(makeExperiencePurchaseItem(experience));
            }

            const selectedTimeslot = timeslots?.find((timeslot) => timeslot.time === selectedTimeslotTime);

            if (selectedTimeslot) {
                setArrival({ date: selectedTimeslot?.date, time: selectedTimeslot?.time }, selectedTimeslot);
            }
        }

        if (isNewPurchase) {
            history.push("/cart");
        } else {
            onClose();
        }
    };

    return (
        <>
            <Modal.Header>Spots Available</Modal.Header>
            <Modal.Body>
                <div className="space-y-10">
                    The following events have the capacity to accommodate this group. Please select an event to continue
                    the purchase.
                </div>
                <ArrivalTimePicker
                    value={selectedTimeslotTime}
                    timeslots={timeslots ?? []}
                    minQuantity={quantity}
                    onChange={setSelectedTimeslotTime}
                />
            </Modal.Body>

            <Modal.Footer className="space-x-4">
                <Tooltip content={tootltipContent}>
                    <SubmitButton color="secondary" variant="outline" isSuccess={isSuccess} onClick={onSubmit}>
                        Join Waitlist
                    </SubmitButton>
                </Tooltip>
                <SubmitButton isLoading={isLoading} onClick={handleExperienceSelect}>
                    Confirm
                </SubmitButton>
            </Modal.Footer>
        </>
    );
};

// Placing this here instead of parent component to avoid dependency cycle
export interface JoinWaitlistPayload {
    customerName: string;
    email: string;
    currency: string;
    phone: string;
    seller: { id: ID };
    items: any[];
    lineItems: any[];
    status: string;
}
